import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PartnerCertificate, PartnerCertificateUsage, CertificateHandlerCheckResultContainer, CertificateHandlerParseResult } from '../models';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { FileHttpService } from '../../shared/http/file-http.service';
import { DownloaderService } from '../../shared/download/downloader.service';
import { switchMap } from 'rxjs/operators';
import { from } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PartnerCertificateService {
    constructor(private httpService: JsonHttpService, private fileHttpService: FileHttpService, private downloaderService: DownloaderService) { }

    /**
     * Get the available certificates for the given partner-id
     */
    getCertificates(partnerId: number): Promise<PartnerCertificate[]> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        return this.httpService.get('/api/partners/certificates/get-certificates', options).toPromise();
    }

    getCertificateUsages(): Promise<PartnerCertificateUsage[]> {
        return this.httpService.get('/api/partners/certificates/get-certificate-usages').toPromise();
    }

    /**
     * Saves the given PartnerCertificate
     * @param certificate The PartnerCertificate to save
     */
    saveCertificate(certificate: PartnerCertificate): Promise<PartnerCertificate> {
        return this.httpService.post('/api/partners/certificates/save-certificate', certificate).toPromise();
    }

    deleteCertificate(certificateId: number): Promise<void> {
        const options = { params: new HttpParams().set('certificateId', `${certificateId}`) };
        return this.httpService.post('/api/partners/certificates/delete-certificate', null, options).toPromise();
    }

    uploadAndCheckCertificate(certificateFile: File): Promise<CertificateHandlerCheckResultContainer> {
        return this.fileHttpService.upload<CertificateHandlerCheckResultContainer>('/api/partners/certificates/upload-and-check-certificate', [certificateFile]);
    }

    parseCertificate(checkResultToken: string, password?: string): Promise<CertificateHandlerParseResult> {
        const options = { params: new HttpParams().set('checkResultToken', `${checkResultToken}`) };
        return this.httpService.post('/api/partners/certificates/parse-certificate', { password }, options).toPromise();
    }

    commitUploadedCertificates(certificates: PartnerCertificate[], certificateToken: string): Promise<void> {
        const options = { params: new HttpParams().set('certificateToken', `${certificateToken}`) };
        return this.httpService.post('/api/partners/certificates/commit-new-certificate', certificates, options).toPromise();
    }

    downloadCertificate(certificateId: number): Promise<void> {
        const options = { params: new HttpParams().set('certificateId', `${certificateId}`) };
        return this.httpService.getTyped<string>('/api/partners/certificates/get-download-token', options).pipe(
            switchMap(token => from(this.downloaderService.downloadFile(token)))
        ).toPromise();
    }
}
